import axios from "axios";
import { API_URL_OBJECTIVES } from "../apiURLS";
import {
  CheckInCreateDto,
  KeyResultCreateDto,
  KeyResultDto,
  KeyResultUpdateDto,
  ObjectiveCreateDto,
  ObjectiveDto,
  ObjectiveUpdateDto,
  PersonalObjectiveCreateDto,
} from "./objectives.types";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("daex-auth-token")}`,
});

// Lists and creation per owner type

const getPersonalObjectives = async (): Promise<ObjectiveDto[]> => {
  const response = await axios.get(`${API_URL_OBJECTIVES}/personal`, {
    headers: authHeaders(),
  });
  if (response.data) return response.data;
  throw new Error("Fetching failed, no data received.");
};

// "Jetzt" objectives of all groups and organizations the user may read
const getAreaObjectives = async (): Promise<ObjectiveDto[]> => {
  const response = await axios.get(`${API_URL_OBJECTIVES}/areas`, {
    headers: authHeaders(),
  });
  if (response.data) return response.data;
  throw new Error("Fetching failed, no data received.");
};

const createPersonalObjective = async (
  data: PersonalObjectiveCreateDto,
): Promise<ObjectiveDto> => {
  const response = await axios.post(`${API_URL_OBJECTIVES}/personal`, data, {
    headers: authHeaders(),
  });
  if (response.data) return response.data;
  throw new Error("Creating failed, no data received.");
};

const getGroupObjectives = async (groupId: string): Promise<ObjectiveDto[]> => {
  const response = await axios.get(`${API_URL_OBJECTIVES}/group/${groupId}`, {
    headers: authHeaders(),
  });
  if (response.data) return response.data;
  throw new Error("Fetching failed, no data received.");
};

const createGroupObjective = async (
  groupId: string,
  data: ObjectiveCreateDto,
): Promise<ObjectiveDto> => {
  const response = await axios.post(
    `${API_URL_OBJECTIVES}/group/${groupId}`,
    data,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Creating failed, no data received.");
};

const getOrganizationObjectives = async (
  organizationId: string,
): Promise<ObjectiveDto[]> => {
  const response = await axios.get(
    `${API_URL_OBJECTIVES}/organization/${organizationId}`,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Fetching failed, no data received.");
};

const createOrganizationObjective = async (
  organizationId: string,
  data: ObjectiveCreateDto,
): Promise<ObjectiveDto> => {
  const response = await axios.post(
    `${API_URL_OBJECTIVES}/organization/${organizationId}`,
    data,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Creating failed, no data received.");
};

// Single objective

const getObjective = async (objectiveId: string): Promise<ObjectiveDto> => {
  const response = await axios.get(`${API_URL_OBJECTIVES}/${objectiveId}`, {
    headers: authHeaders(),
  });
  if (response.data) return response.data;
  throw new Error("Fetching failed, no data received.");
};

const updateObjective = async (
  objectiveId: string,
  data: ObjectiveUpdateDto,
): Promise<ObjectiveDto> => {
  const response = await axios.put(
    `${API_URL_OBJECTIVES}/${objectiveId}`,
    data,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Updating failed, no data received.");
};

const deleteObjective = async (objectiveId: string): Promise<string> => {
  await axios.delete(`${API_URL_OBJECTIVES}/${objectiveId}`, {
    headers: authHeaders(),
  });
  return objectiveId;
};

// Key results

const addKeyResult = async (
  objectiveId: string,
  data: KeyResultCreateDto,
): Promise<KeyResultDto> => {
  const response = await axios.post(
    `${API_URL_OBJECTIVES}/${objectiveId}/keyResults`,
    data,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Creating failed, no data received.");
};

const updateKeyResult = async (
  objectiveId: string,
  keyResultId: string,
  data: KeyResultUpdateDto,
): Promise<KeyResultDto> => {
  const response = await axios.put(
    `${API_URL_OBJECTIVES}/${objectiveId}/keyResults/${keyResultId}`,
    data,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Updating failed, no data received.");
};

const deleteKeyResult = async (
  objectiveId: string,
  keyResultId: string,
): Promise<string> => {
  await axios.delete(
    `${API_URL_OBJECTIVES}/${objectiveId}/keyResults/${keyResultId}`,
    { headers: authHeaders() },
  );
  return keyResultId;
};

// Check-ins

const createCheckIn = async (
  objectiveId: string,
  data: CheckInCreateDto,
): Promise<ObjectiveDto> => {
  const response = await axios.post(
    `${API_URL_OBJECTIVES}/${objectiveId}/checkIns`,
    data,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Check-in failed, no data received.");
};

// Linked projects and tasks (the backend returns the updated objective)

const linkProject = async (
  objectiveId: string,
  projectId: string,
  keyResultId?: string,
): Promise<ObjectiveDto> => {
  const response = await axios.put(
    `${API_URL_OBJECTIVES}/${objectiveId}/projects/${projectId}`,
    { keyResultId },
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Linking failed, no data received.");
};

const unlinkProject = async (
  objectiveId: string,
  projectId: string,
): Promise<ObjectiveDto> => {
  const response = await axios.delete(
    `${API_URL_OBJECTIVES}/${objectiveId}/projects/${projectId}`,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Unlinking failed, no data received.");
};

const linkTask = async (
  objectiveId: string,
  taskId: string,
  keyResultId?: string,
): Promise<ObjectiveDto> => {
  const response = await axios.put(
    `${API_URL_OBJECTIVES}/${objectiveId}/tasks/${taskId}`,
    { keyResultId },
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Linking failed, no data received.");
};

const unlinkTask = async (
  objectiveId: string,
  taskId: string,
): Promise<ObjectiveDto> => {
  const response = await axios.delete(
    `${API_URL_OBJECTIVES}/${objectiveId}/tasks/${taskId}`,
    { headers: authHeaders() },
  );
  if (response.data) return response.data;
  throw new Error("Unlinking failed, no data received.");
};

const objectivesService = {
  getAreaObjectives,
  linkProject,
  unlinkProject,
  linkTask,
  unlinkTask,
  getPersonalObjectives,
  createPersonalObjective,
  getGroupObjectives,
  createGroupObjective,
  getOrganizationObjectives,
  createOrganizationObjective,
  getObjective,
  updateObjective,
  deleteObjective,
  addKeyResult,
  updateKeyResult,
  deleteKeyResult,
  createCheckIn,
};
export default objectivesService;
